import React, { useEffect, useState, useRef } from "react";
import CustomInput from "../components/CustomInput";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import { useFormik } from "formik";
import axios from "axios";
import { base_url } from "../utils/baseUrl";

// Validation schema
const schema = yup.object().shape({
  code: yup.string().required("Discount Code is Required"),
  discount_percentage: yup.number().nullable(),
  discount_flat: yup.number().nullable(),
  description: yup.string().required("Description is Required"),
  min_amount: yup.number().required("Minimum Amount is Required").min(0),
  max_amount: yup.number().required("Maximum Amount is Required").min(0)
    .test('greater-than-min', 'Maximum amount must be greater than minimum amount',
      function (value) {
        return !value || !this.parent.min_amount || value >= this.parent.min_amount;
      }),
  valid_from: yup.date().required("Valid From date is Required"),
  valid_to: yup.date().required("Valid To date is Required"),
  max_usage: yup.number().min(0).required("Maximum Usage is Required"),
  show_on_frontend: yup.boolean(),
}).test('one-discount-type', 'Either percentage or flat discount is required', function (values) {
  return (values.discount_percentage === null && values.discount_flat !== null) ||
    (values.discount_percentage !== null && values.discount_flat === null);
});

const AddDiscount = () => {
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [image, setImage] = useState(null); // Only one image
  const [discountType, setDiscountType] = useState('percentage'); // Add this state

  // Trigger file input click
  const handleImageClick = () => {
    inputRef.current.click();
  };

  // Handle file change and update state
  const handleFileChange = (e) => {
    const file = e.target.files[0]; // Only take the first selected image
    if (file) {
      setImage({
        file,
        url: URL.createObjectURL(file),
      });
    }
  };

  const adminToken = localStorage.getItem("adminToken");
  console.log("Admin Token:", adminToken);

  useEffect(() => {
    const adminToken = localStorage.getItem("adminToken");
    if (!adminToken) {
      navigate("/");
    }
  }, [navigate]);

  // Remove image from state
  const handleImageRemove = () => {
    setImage(null); // Remove the image
  };

  const formik = useFormik({
    initialValues: {
      code: "",
      discount_percentage: null,
      discount_flat: null,
      description: "",
      min_amount: null,
      max_amount: null,
      valid_from: "",
      valid_to: "",
      max_usage: 0,
      show_on_frontend: false,
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      try {
        const response = await axios.post(`${base_url}/api/discounts/createDiscount`, values);
        toast.success("Discount Added Successfully!");
        navigate("/admin/coupon-list");  // Redirect to the discounts page after success
      } catch (error) {
        console.error("Error adding discount:", error);
        toast.error("Something Went Wrong!");
      }
    },
  });

  return (
    <div>
      <h3 className="mb-4 title">Add Coupon</h3>
      <form onSubmit={formik.handleSubmit} className="d-flex gap-3 flex-column">
        {/* Discount Code */}
        <CustomInput
          type="text"
          label="Enter Coupon Code"
          name="code"
          onChng={formik.handleChange("code")}
          onBlr={formik.handleBlur("code")}
          val={formik.values.code}
        />
        <div className="error">{formik.touched.code && formik.errors.code}</div>

        {/* Discount Type */}
        <div className="form-group">
          <label>Discount Type</label>
          <div className="d-flex gap-3 mb-3">
            <div className="form-check">
              <input
                type="radio"
                id="percentage"
                name="discountType"
                className="form-check-input"
                checked={discountType === 'percentage'}
                onChange={() => {
                  setDiscountType('percentage');
                  formik.setFieldValue("discount_flat", null);
                }}
              />
              <label className="form-check-label" htmlFor="percentage">
                Percentage
              </label>
            </div>
            <div className="form-check">
              <input
                type="radio"
                id="flat"
                name="discountType"
                className="form-check-input"
                checked={discountType === 'flat'}
                onChange={() => {
                  setDiscountType('flat');
                  formik.setFieldValue("discount_percentage", null);
                }}
              />
              <label className="form-check-label" htmlFor="flat">
                Flat Amount
              </label>
            </div>
          </div>

          {discountType === 'percentage' ? (
            <CustomInput
              type="number"
              label="Percentage Discount"
              name="discount_percentage"
              onChng={(e) => {
                formik.setFieldValue("discount_percentage", e.target.value);
                formik.setFieldValue("discount_flat", null);
              }}
              onBlr={formik.handleBlur("discount_percentage")}
              val={formik.values.discount_percentage}
            />
          ) : (
            <CustomInput
              type="number"
              label="Flat Discount Amount"
              name="discount_flat"
              onChng={(e) => {
                formik.setFieldValue("discount_flat", e.target.value);
                formik.setFieldValue("discount_percentage", null);
              }}
              onBlr={formik.handleBlur("discount_flat")}
              val={formik.values.discount_flat}
            />
          )}
        </div>

        {/* Description */}
        <CustomInput
          type="text"
          label="Description"
          name="description"
          onChng={formik.handleChange("description")}
          onBlr={formik.handleBlur("description")}
          val={formik.values.description}
        />
        <div className="error">{formik.touched.description && formik.errors.description}</div>

        {/* Minimum Amount */}
        <CustomInput
          type="number"
          label="Minimum Amount"
          name="min_amount"
          onChng={formik.handleChange("min_amount")}
          onBlr={formik.handleBlur("min_amount")}
          val={formik.values.min_amount}
        />
        <div className="error">{formik.touched.min_amount && formik.errors.min_amount}</div>

        {/* Maximum Amount */}
        <CustomInput
          type="number"
          label="Maximum Amount"
          name="max_amount"
          onChng={formik.handleChange("max_amount")}
          onBlr={formik.handleBlur("max_amount")}
          val={formik.values.max_amount}
        />
        <div className="error">{formik.touched.max_amount && formik.errors.max_amount}</div>

        {/* Valid From Date */}
        <CustomInput
          type="date"
          label="Enter Valid From Date"
          name="valid_from"
          onChng={formik.handleChange("valid_from")}
          onBlr={formik.handleBlur("valid_from")}
          val={formik.values.valid_from}
        />
        <div className="error">{formik.touched.valid_from && formik.errors.valid_from}</div>

        {/* Valid To Date */}
        <CustomInput
          type="date"
          label="Enter Valid To Date"
          name="valid_to"
          onChng={formik.handleChange("valid_to")}
          onBlr={formik.handleBlur("valid_to")}
          val={formik.values.valid_to}
        />
        <div className="error">{formik.touched.valid_to && formik.errors.valid_to}</div>

        {/* Maximum Usage */}
        <CustomInput
          type="number"
          label="Enter Maximum Usage (0 for Unlimited)"
          name="max_usage"
          onChng={formik.handleChange("max_usage")}
          onBlr={formik.handleBlur("max_usage")}
          val={formik.values.max_usage}
        />
        <div className="error">{formik.touched.max_usage && formik.errors.max_usage}</div>

        {/* Image Upload Section */}
        <div className="d-flex flex-wrap">
          {image && (
            <div key={0} className="position-relative m-2">
              <img src={image.url} alt="discount" className="img-fluid" style={{ width: '100px', height: "100px" }} />
              <button
                type="button"
                className="btn btn-danger position-absolute top-0 end-0"
                onClick={handleImageRemove}
              >
                X
              </button>
            </div>
          )}
        </div>

        {/* Show on Frontend Checkbox */}
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id="show_on_frontend"
            name="show_on_frontend"
            checked={formik.values.show_on_frontend}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          <label className="form-check-label" htmlFor="show_on_frontend">
            Show on Frontend
          </label>
        </div>

        {/* Submit Button */}
        <button type="submit" className="btn btn-success">
          Add Coupon
        </button>
      </form>
    </div>
  );
};

export default AddDiscount;
